/**
 * "X is typing" for one channel.
 *
 * Listens for `typing.changed` on the relay channel the conversation hangs off (its
 * order, or its home) — the channel a whole order's threads share, so an event is
 * matched to THIS conversation by its `channelId`, never by the relay channel alone; an
 * unmatched indicator would show the internal-notes typists inside the homeowner thread.
 *
 * Own events are dropped: the composer announces the viewer's typing, and the echo would
 * show the viewer typing to themselves.
 *
 * The event names the typist by id only. Their name and avatar come from the channel's
 * members, read only while someone is typing. A typist missing from the cached members
 * (they joined, by reading the thread, after the list was cached) triggers one refetch;
 * until it lands, or if they are still missing, they show as a blank avatar.
 *
 * A stop is best-effort: a typist who force-closes the tab never sends one.
 * `TYPING_TTL_MS` is the backstop that clears them regardless. The channel `key` in
 * `ChannelView` still remounts this component on channel change, so no event from the
 * old channel can survive the switch even within the TTL window.
 */

import { useGetAuthMe } from "@birdwatch/client";
import { isRealtimeChannelAllowed } from "@birdwatch/common";
import { useEffect, useMemo, useRef, useState } from "react";

import { UserTyping } from "@/components/ui/message";
import { useRealtimeRelay } from "@/features/shared/realtime-relay";
import type { Channel } from "../_api/channel-endpoints";
import { useChannelMembers } from "../_api/use-channel";

/**
 * A typist with no fresh event for this long is dropped. The sender re-announces typing
 * at most every 3s while still typing (`useTypingStatus`), so a continuously-typing user
 * never expires; one who walks off mid-message clears even though no stop came.
 */
const TYPING_TTL_MS = 8_000;

/** How often expired typists are swept. Only runs while someone is typing. */
const TYPING_SWEEP_MS = 1_000;

/** Typist `userId` → when they stop counting as typing (epoch ms). */
type Typists = Map<string, number>;

/** Pure — returns the SAME reference when nothing expired, so `setState` bails out. */
function pruneExpired(entries: Typists, now: number): Typists {
  const live = new Map(
    Array.from(entries).filter(([, expiresAt]) => expiresAt > now),
  );
  return live.size === entries.size ? entries : live;
}

type ChannelTypingProps = {
  cid: string;
  /** The open conversation: `id` matches events, `entityId` is the relay channel. */
  channel: Pick<Channel, "id" | "entityId">;
  className?: string;
};

export function ChannelTyping({ cid, channel, className }: ChannelTypingProps) {
  const [typists, setTypists] = useState<Typists>(new Map());
  const { data: me } = useGetAuthMe().get();

  useRealtimeRelay({
    // A channel the relay would refuse stops every hook sharing the tab's stream.
    channels: isRealtimeChannelAllowed(channel.entityId)
      ? [channel.entityId]
      : [],
    events: ["typing.changed"],
    onData: (payload) => {
      // Read through `payload`, never destructured: the union narrows on `event`.
      if (payload.event !== "typing.changed") return;
      const { channelId, userId, typing } = payload.data;
      if (channelId !== channel.id || userId === me?.id) return;

      if (typing) {
        setTypists((previous) =>
          new Map(previous).set(userId, Date.now() + TYPING_TTL_MS),
        );
        return;
      }

      setTypists((previous) => {
        // Same reference when the id was never there — React skips the re-render.
        if (!previous.has(userId)) return previous;
        const next = new Map(previous);
        next.delete(userId);
        return next;
      });
    },
  });

  // Gate on the emptiness boolean, not the map itself — depending on the map would
  // restart the interval on every keystroke event.
  const hasTypists = typists.size > 0;
  useEffect(() => {
    if (!hasTypists) return;
    const timer = setInterval(() => {
      setTypists((previous) => pruneExpired(previous, Date.now()));
    }, TYPING_SWEEP_MS);
    return () => clearInterval(timer);
  }, [hasTypists]);

  const {
    data: members,
    isFetching: isFetchingMembers,
    refetch: refetchMembers,
  } = useChannelMembers(cid, hasTypists);
  const usersById = useMemo(
    () =>
      new Map(
        (members ?? []).flatMap((member) =>
          member.user ? [[member.userId, member.user] as const] : [],
        ),
      ),
    [members],
  );

  // Key on ids ONLY. Every TTL refresh rewrites the expiry, so keying on more would
  // remount `UserTyping` on each event and restart the bounce animation.
  const typistIds = Array.from(typists.keys());
  const typistKey = [...typistIds].sort().join(",");

  /** Typists the members were already refetched for — one refetch each, never a loop. */
  const refetchedForRef = useRef(new Set<string>());
  useEffect(() => {
    if (!members || isFetchingMembers || !typistKey) return;
    const unknownIds = typistKey
      .split(",")
      .filter(
        (userId) =>
          !usersById.has(userId) && !refetchedForRef.current.has(userId),
      );
    if (unknownIds.length === 0) return;
    for (const userId of unknownIds) refetchedForRef.current.add(userId);
    void refetchMembers();
  }, [members, isFetchingMembers, typistKey, usersById, refetchMembers]);

  if (!hasTypists) return null;

  return (
    <UserTyping
      key={typistKey}
      users={typistIds.map((userId) => {
        const user = usersById.get(userId);
        return {
          name: user?.name ?? user?.firstName ?? "",
          avatarUrl: user?.avatarUrl,
        };
      })}
      className={className}
    />
  );
}
